import type { ReactElement, ReactNode } from 'react';
import type {
  DistributiveOmit,
  PropsOf,
  SerializedStyles,
} from '@emotion/react';
import styled from '@emotion/styled';
import type { DismissibleProps } from '@common/Dismissible';
import { CloseAction } from '@components/Actions/CloseAction/CloseAction';
import { NotificationIcon } from './NotificationIcon';
import { NotificationLabel } from './NotificationLabel';
import {
  BACKGROUND_COLORS,
  baseNotificationStyles,
  closeActionStyles,
} from './styles';
import { useInterpolate } from '@hooks/useInterpolate';
import { useStrings } from '@hooks/useStrings';

type NotificationType = 'alert' | 'banner' | 'toast';

export type NotificationKind = 'info' | 'danger' | 'success' | 'warning';

interface CommonBaseNotificationProps extends PropsOf<'div'> {
  /**
   * Kind of notification. This will be the intent of the message.
   */
  kind?: NotificationKind;
  /**
   * Styles override.
   */
  className?: string;
}

interface CustomBaseNotificationProps extends CommonBaseNotificationProps {
  /**
   * Content shown in the notification.
   */
  children: ReactNode;
  /**
   * The type of notification
   */
  notificationType: NotificationType;
  /**
   * The name of the notification, used in the close action description
   */
  notificationName?: string;
  /**
   * Styles for the notification inner wrapper. Implies an additional
   * `<div>` wrapper will be used within the outer container of the
   * notification. If omitted, no inner wrapper will be used.
   */
  wrapperCss?: SerializedStyles;
  /**
   * Whether to wrap the passed content in `NotificationLabel`. Default `false`.
   * @see NotificationLabel
   */
  wrapContentWithLabel?: boolean;
  /**
   * Styles for the close action, if present.
   */
  closeActionCss?: SerializedStyles;
}

interface LimitedBaseNotificationProps extends CommonBaseNotificationProps {
  /**
   * Text shown in the notification.
   */
  children: string | ReactElement;
}

export type BaseNotificationProps<T extends boolean = boolean> =
  CustomBaseNotificationProps & DismissibleProps<T>;

type ConsumedBaseNotificationProps<T extends boolean = boolean> =
  LimitedBaseNotificationProps & DismissibleProps<T>;

export type SpecificNotificationProps<
  TDismissible extends boolean = boolean,
  RemoveNotificationProps extends keyof ConsumedBaseNotificationProps = never,
> = DistributiveOmit<ConsumedBaseNotificationProps, RemoveNotificationProps> &
  DismissibleProps<TDismissible> & {
    /**
     * Kind of notification. This will be the intent of the message.
     */
    kind?: NotificationKind;
  };

const StyledNotification = styled('div', {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: NotificationKind }>`
  ${baseNotificationStyles.styles}
  background-color: ${({ kind }) => BACKGROUND_COLORS[kind]};
`;

/**
 * BaseNotification component.
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 * @summary Provides a low-level implementation for notification components.
 * @see Alert
 * @see Banner
 * @see Toast
 * @private
 */
export const BaseNotification = <T extends boolean>({
  children,
  kind = 'info',
  notificationType,
  wrapperCss,
  wrapContentWithLabel = false,
  notificationName,
  isDismissible,
  onDismiss,
  closeActionCss,
  ...props
}: BaseNotificationProps<T>): JSX.Element => {
  const { notification } = useStrings();
  const interpolate = useInterpolate();

  const inner = (
    <>
      <NotificationIcon kind={kind} />
      {wrapContentWithLabel ? (
        <NotificationLabel>{children}</NotificationLabel>
      ) : (
        children
      )}
    </>
  );

  const notificationDescriptionName =
    typeof notificationName === 'undefined'
      ? notificationType
      : notificationName;

  return (
    <StyledNotification
      {...props}
      kind={kind}
      data-testid={`${notificationType}-${kind}`}
    >
      {wrapperCss ? <div css={wrapperCss}>{inner}</div> : inner}

      {isDismissible && (
        <CloseAction
          onClick={onDismiss}
          description={interpolate(notification.closeActionDescription, {
            kind,
            name: notificationDescriptionName,
          })}
          css={[closeActionStyles, closeActionCss]}
          className={`${notificationType}-${kind}-close`}
        />
      )}
    </StyledNotification>
  );
};
